
import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

interface Article {
  id: number;
  category: string;
  title: string;
  description: string;
  content: string;
  icon: string;
  color: string;
}

export default function BeginnerPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

  /* --------------------------------------------------------------
   *  Scroll handling – moved to useEffect so the listener is added
   *  only once and removed on unmount. This also prevents the
   *  “Unexpected closing …” JSX errors that were caused by the
   *  listener being attached during every render.
   * -------------------------------------------------------------- */
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const articles: Article[] = [
    {
      id: 1,
      category: '暗号通貨とは？',
      title: '暗号通貨（仮想通貨）の基礎知識',
      description:
        'ビットコインやイーサリアムなど、デジタルなお金の仕組みを初心者向けに解説します。',
      icon: 'ri-coin-line',
      color: 'from-pink-500 to-rose-500',
      content: `
        <h2 class="text-3xl font-bold mb-6 text-gray-800">暗号通貨（仮想通貨）とは？</h2>
        <!-- ... 省略 (元の HTML コンテンツはそのまま) ... -->
      `,
    },
    {
      id: 2,
      category: 'ブロックチェーンとは？',
      title: 'ブロックチェーン技術の仕組み',
      description:
        '暗号通貨を支える革新的な技術、ブロックチェーンについて分かりやすく説明します。',
      icon: 'ri-links-line',
      color: 'from-purple-500 to-indigo-500',
      content: `
        <h2 class="text-3xl font-bold mb-6 text-gray-800">ブロックチェーンとは？</h2>
        <!-- ... 省略 ... -->
      `,
    },
    {
      id: 3,
      category: 'ウォレットとは？',
      title: 'ウォレットの種類と使い方',
      description:
        '暗号通貨を保管するウォレットの基本から、安全な管理方法まで詳しく解説します。',
      icon: 'ri-wallet-3-line',
      color: 'from-blue-500 to-cyan-500',
      content: `
        <h2 class="text-3xl font-bold mb-6 text-gray-800">ウォレットとは？</h2>
        <!-- ... 省略 ... -->
      `,
    },
    {
      id: 4,
      category: 'DePINとは？',
      title: 'DePIN（分散型物理インフラ）の未来',
      description:
        'MAWARIが取り組むDePINプロジェクトについて、その仕組みと可能性を解説します。',
      icon: 'ri-server-line',
      color: 'from-emerald-500 to-teal-500',
      content: `
        <h2 class="text-3xl font-bold mb-6 text-gray-800">DePINとは？</h2>
        <!-- ... 省略 ... -->
      `,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-white to-rose-50">
      <Navbar isScrolled={isScrolled} />

      {/* パンくずリスト */}
      <div className="pt-32 pb-6 px-6 max-w-7xl mx-auto">
        <div className="flex items-center space-x-2 text-base text-gray-600">
          <a href="/" className="hover:text-pink-600 transition-colors cursor-pointer">
            TOP
          </a>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gray-800 font-semibold">初心者講座</span>
        </div>
      </div>

      {/* ヘッダーセクション */}
      <div className="px-6 pb-16 max-w-7xl mx-auto">
        <div className="text-center mb-4">
          <div className="inline-block px-6 py-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-full text-base font-bold mb-6">
            🎓 初心者講座
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-800 mb-6">
            暗号通貨の基礎をやさしく学ぼう
          </h1>
          <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
            暗号通貨・ブロックチェーン・ウォレット・DePIN。難しそうな言葉も、ここで学べば大丈夫。<strong>初心者・シニアの方でも安心して学べる</strong>内容です。
          </p>
        </div>
      </div>

      {/* カテゴリカード一覧 */}
      {!selectedArticle && (
        <div className="px-6 pb-20 max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {articles.map((article) => (
              <div
                key={article.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer group"
                onClick={() => setSelectedArticle(article)}
              >
                <div className={`h-3 bg-gradient-to-r ${article.color}`}></div>
                <div className="p-8">
                  <div
                    className={`w-16 h-16 flex items-center justify-center bg-gradient-to-r ${article.color} rounded-2xl mb-6 group-hover:scale-110 transition-transform`}
                  >
                    <i className={`${article.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">{article.category}</h3>
                  <h4 className="text-xl font-semibold text-gray-700 mb-4">{article.title}</h4>
                  <p className="text-lg text-gray-600 leading-relaxed mb-6">{article.description}</p>
                  <div className="flex items-center text-pink-600 font-bold text-lg group-hover:translate-x-2 transition-transform">
                    詳しく見る
                    <i className="ri-arrow-right-line ml-2"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 記事詳細表示 */}
      {selectedArticle && (
        <div className="px-6 pb-20 max-w-5xl mx-auto">
          <button
            onClick={() => setSelectedArticle(null)}
            className="flex items-center text-lg text-gray-600 hover:text-pink-600 transition-colors mb-8 cursor-pointer font-semibold"
          >
            <i className="ri-arrow-left-line mr-2 text-xl"></i>
            カテゴリ一覧に戻る
          </button>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className={`h-4 bg-gradient-to-r ${selectedArticle.color}`}></div>
            <div className="p-10 md:p-16">
              <div className="flex items-center mb-6">
                <div
                  className={`w-14 h-14 flex items-center justify-center bg-gradient-to-r ${selectedArticle.color} rounded-xl mr-4`}
                >
                  <i className={`${selectedArticle.icon} text-2xl text-white`}></i>
                </div>
                <div>
                  <p className="text-base font-bold text-pink-600 mb-1">{selectedArticle.category}</p>
                  <h2 className="text-2xl font-bold text-gray-800">{selectedArticle.title}</h2>
                </div>
              </div>

              <div
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
              />

              <div className="mt-12 pt-8 border-t-2 border-gray-200">
                <button
                  onClick={() => setSelectedArticle(null)}
                  className="w-full md:w-auto px-10 py-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-lg rounded-full font-bold hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  他のカテゴリも見る
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA セクション */}
      {!selectedArticle && (
        <div className="px-6 pb-20 max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-3xl p-12 md:p-16 text-center text-white shadow-2xl">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">もっと詳しく知りたい方へ</h2>
            <p className="text-xl leading-relaxed mb-10 max-w-3xl mx-auto">
              MAWARI Freakコミュニティでは、定期的にZoom勉強会を開催しています。わからないことは、いつでも質問できます！
            </p>
            <div className="flex flex-col md:flex-row gap-6 justify-center">
              <a
                href="/community"
                className="px-10 py-5 bg-white text-pink-600 text-lg rounded-full font-bold hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
              >
                コミュニティに参加
              </a>
              <a
                href="/contact"
                className="px-10 py-5 bg-transparent border-3 border-white text-white text-lg rounded-full font-bold hover:bg-white hover:text-pink-600 transition-all cursor-pointer whitespace-nowrap"
              >
                お問い合わせ
              </a>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
